import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";
import { ArrowDown, Phone } from "lucide-react";
import Stamp from "./Stamp";
import { PHONE_TEL, PHONE_DISPLAY, waLink, scrollToSection } from "../lib/scroll";

const EASE = [0.16, 1, 0.3, 1];
const INTRO = 1.05; // seconds — matches curtain lift

const lineVariants = {
  hidden: { y: "115%", rotate: 2 },
  show: (i) => ({
    y: "0%",
    rotate: 0,
    transition: { duration: 1.1, ease: EASE, delay: INTRO + i * 0.13 },
  }),
};

function MaskedLine({ index, children, className = "" }) {
  return (
    <div className="overflow-hidden py-[0.5vw]">
      <motion.span
        custom={index}
        variants={lineVariants}
        initial="hidden"
        animate="show"
        className={`block origin-left ${className}`}
      >
        {children}
      </motion.span>
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 14 });

  const onTilt = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <div className="max-w-[1440px] w-full mx-auto px-5 sm:px-10 pt-28 sm:pt-36 pb-10 sm:pb-16 grid lg:grid-cols-12 gap-12 lg:gap-8 items-end">
        {/* Left — kinetic type */}
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: INTRO - 0.15 }}
            className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-rust mb-6 sm:mb-8 flex items-center gap-3"
          >
            <span className="w-8 h-px bg-rust inline-block" />
            Small-batch bakery — Dadar, Mumbai
          </motion.p>

          <h1 className="font-display font-black leading-[0.92] tracking-tight text-[17vw] sm:text-[13vw] lg:text-[7.6rem]">
            <MaskedLine index={0}>Butter,</MaskedLine>
            <MaskedLine index={1}>
              flour <span className="font-garamond italic font-medium">&amp;</span>
            </MaskedLine>
            <MaskedLine index={2}>
              <span className="italic text-rust">patience.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: INTRO + 0.55 }}
            className="mt-6 sm:mt-8 max-w-md text-sm sm:text-base text-browntext leading-relaxed"
          >
            Zoni&apos;s Bakehouse is a home bakery in Dadar. Mini Basque
            cheesecakes, chocochunk cookie pies, pave chocolates and olive
            focaccia — baked in tiny batches, to order.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: INTRO + 0.7 }}
            className="mt-8 sm:mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={waLink("Hi Zoni's Bakehouse! I'd like to place an order.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-order-button"
              className="group bg-rust text-eggshell px-7 py-4 rounded-full font-mono text-[11px] sm:text-xs uppercase tracking-[0.2em] hover:bg-ink transition-colors duration-300"
            >
              Order on WhatsApp
            </a>
            <a
              href={PHONE_TEL}
              data-testid="hero-click-to-call-button"
              className="flex items-center gap-2.5 border-[1.5px] border-ink px-7 py-4 rounded-full font-mono text-[11px] sm:text-xs uppercase tracking-[0.2em] hover:bg-ink hover:text-eggshell transition-colors duration-300"
            >
              <Phone size={14} /> {PHONE_DISPLAY}
            </a>
          </motion.div>
        </motion.div>

        {/* Right — spotlight photo with parallax + 3D tilt stamp */}
        <div className="lg:col-span-5 relative flex justify-center lg:justify-end" onMouseMove={onTilt} onMouseLeave={() => { mx.set(0); my.set(0); }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: INTRO + 0.3, ease: EASE }}
            style={{ y: imgY, opacity: imgOpacity }}
            className="relative"
          >
            <div className="w-[78vw] max-w-[340px] sm:max-w-[400px] lg:w-[30vw] xl:w-[420px] aspect-[3/4] rounded-t-[999px] rounded-b-[28px] overflow-hidden border-[5px] border-ink shadow-[12px_12px_0_#8C3B1B]">
              <motion.img
                src="/photos/cheesecake.jpg"
                alt="Zoni's Basque burnt cheesecakes on the Bakehouse sticker sheet"
                className="w-full h-full object-cover"
                initial={{ scale: 1.25 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.8, delay: INTRO + 0.3, ease: EASE }}
              />
            </div>

            <motion.div
              style={{ rotateX: rx, rotateY: ry, transformPerspective: 600 }}
              className="absolute -top-8 -left-6 sm:-left-12 z-10"
            >
              <Stamp size={116} className="drop-shadow-[6px_6px_0_rgba(28,21,18,0.15)]" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: INTRO + 0.9 }}
              className="absolute -bottom-5 -left-4 sm:-left-10 bg-eggshell border-[1.5px] border-ink rounded-full px-5 py-2.5 flex items-center gap-2.5 shadow-[6px_6px_0_rgba(28,21,18,0.12)]"
            >
              <span className="relative flex w-2.5 h-2.5">
                <span className="animate-soft-ping absolute inline-flex h-full w-full rounded-full bg-rust" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rust" />
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em]">
                Baked to order, daily
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.button
        onClick={() => scrollToSection("#manifesto")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: INTRO + 1.2, duration: 0.8 }}
        data-testid="hero-scroll-cue"
        aria-label="Scroll to manifesto"
        className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-mutedbrown hover:text-rust transition-colors"
      >
        Scroll <ArrowDown size={12} className="animate-bounce" />
      </motion.button>
    </section>
  );
}
